'use client';

import { useEffect, useRef } from 'react';

// Reads store config injected by the server on <body>

function getVisitorId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|; )_ls_visitor=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function getBodyConfig(): { storeId: string; apiUrl: string } {
  if (typeof document === 'undefined') return { storeId: '', apiUrl: '' };
  const body = document.body;
  return {
    storeId: body.getAttribute('data-store-id') || '',
    apiUrl: body.getAttribute('data-launchstore-api-url') || '',
  };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

export function useWebPush() {
  const subscribed = useRef(false);

  useEffect(() => {
    if (subscribed.current) return;

    // Check browser support
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !('Notification' in window)
    ) {
      return;
    }

    const { storeId, apiUrl } = getBodyConfig();
    if (!storeId || !apiUrl) return;

    const register = async () => {
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

        // Wait for SW to be ready
        await navigator.serviceWorker.ready;

        // Check existing subscription first — don't re-prompt if already subscribed
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          subscribed.current = true;
          return;
        }

        // Don't ask for permission proactively — only subscribe if already granted
        // (explicit opt-in should be triggered by a user gesture, e.g. a "Enable notifications" button)
        if (Notification.permission !== 'granted') {
          return;
        }

        // Fetch VAPID public key
        const keyRes = await fetch(`${apiUrl}/api/stores/${storeId}/web-push/vapid-public-key`);
        if (!keyRes.ok) return;
        const { publicKey } = await keyRes.json();
        if (!publicKey) return;

        // Subscribe to push
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        // Send subscription to backend
        const p256dhKey = arrayBufferToBase64(subscription.getKey('p256dh')!);
        const authKey = arrayBufferToBase64(subscription.getKey('auth')!);

        await fetch(`${apiUrl}/api/stores/${storeId}/web-push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            p256dhKey,
            authKey,
            userAgent: navigator.userAgent,
            visitorId: getVisitorId(),
          }),
        });

        subscribed.current = true;
      } catch {
        // Silently fail — push notifications are enhancement, not critical path
      }
    };

    register();
  }, []);
}

// Imperative function for explicit opt-in (call from a button click)
export async function requestWebPushPermission(): Promise<'granted' | 'denied' | 'default'> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';

  const { storeId, apiUrl } = getBodyConfig();
  if (!storeId || !apiUrl) return 'denied';

  // Ask for permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;

  try {
    const registration = await navigator.serviceWorker.ready;

    // Fetch VAPID public key
    const keyRes = await fetch(`${apiUrl}/api/stores/${storeId}/web-push/vapid-public-key`);
    if (!keyRes.ok) return permission;
    const { publicKey } = await keyRes.json();
    if (!publicKey) return permission;

    // Subscribe
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    const p256dhKey = arrayBufferToBase64(subscription.getKey('p256dh')!);
    const authKey = arrayBufferToBase64(subscription.getKey('auth')!);

    await fetch(`${apiUrl}/api/stores/${storeId}/web-push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        p256dhKey,
        authKey,
        userAgent: navigator.userAgent,
      }),
    });
  } catch {
    // Silently fail
  }

  return permission;
}
